import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Lock, LogOut, RefreshCw, Trash2, Sparkles, PartyPopper, ListChecks, Star } from "lucide-react";
import { Crescent } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "called", "booked"];
const STATUS_LABEL = { new: "New", called: "Called", booked: "Booked" };
const STATUS_STYLE = {
    new: "bg-brand-olivelight text-brand-olive border-brand-accentline",
    called: "bg-amber-50 text-amber-700 border-amber-200",
    booked: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function apiFetch(path, token, opts = {}) {
    return fetch(`${API}${path}`, {
        ...opts,
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(opts.headers || {}),
        },
    }).then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) throw Object.assign(new Error("session"), { session: true, detail: data.detail });
        if (!res.ok) throw Object.assign(new Error("fail"), { detail: data.detail });
        return data;
    });
}

function fmtDate(iso) {
    try {
        return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return iso;
    }
}

function Login({ onLogin }) {
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const data = await apiFetch("/admin/login", null, {
                method: "POST",
                body: JSON.stringify({ password }),
            });
            onLogin(data.token);
        } catch (err) {
            setError(err.session ? err.detail || "Login failed" : "Incorrect password. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-5">
            <Toaster position="top-center" richColors />
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 shadow-card"
            >
                <div className="mb-6 flex items-center gap-3">
                    <Crescent className="h-8 w-8 text-brand-lime" />
                    <p className="font-display text-2xl font-semibold text-brand-navy">Signita Admin</p>
                </div>
                <label htmlFor="admin-password" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                    Admin password
                </label>
                <div className="relative">
                    <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        id="admin-password"
                        data-testid="admin-login-input"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        className="field-input pl-11"
                    />
                </div>
                {error && (
                    <p data-testid="admin-login-error" className="mt-3 text-xs font-semibold text-red-500">
                        {error}
                    </p>
                )}
                <button
                    data-testid="admin-login-button"
                    type="submit"
                    disabled={busy}
                    className="mt-6 w-full rounded-full bg-brand-navy py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-olive disabled:opacity-60"
                >
                    {busy ? "Checking…" : "Sign in"}
                </button>
            </form>
        </div>
    );
}

function StatusControl({ row, onSet }) {
    return (
        <div className="flex gap-1.5">
            {STATUSES.map((s) => (
                <button
                    key={s}
                    data-testid={`admin-status-${row.id}-${s}`}
                    onClick={() => onSet(row.id, s)}
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-bold transition-all duration-200 ${
                        row.status === s
                            ? STATUS_STYLE[s]
                            : "border-slate-200 bg-white text-slate-400 hover:border-brand-olive hover:text-brand-olive"
                    }`}
                >
                    {STATUS_LABEL[s]}
                </button>
            ))}
        </div>
    );
}

function Enquiries({ token }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const data = await apiFetch("/admin/appointments", token);
            setRows(data);
            setError("");
        } catch (err) {
            if (!err.session) setError("Could not load enquiries.");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setStatus = async (id, status) => {
        setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
        try {
            await apiFetch(`/admin/appointments/${id}/status`, token, {
                method: "PATCH",
                body: JSON.stringify({ status }),
            });
            toast.success(`Marked as ${STATUS_LABEL[status].toLowerCase()}`);
        } catch (err) {
            if (!err.session) {
                toast.error("Could not update status");
                load();
            }
        }
    };

    const remove = async (id) => {
        try {
            await apiFetch(`/admin/appointments/${id}`, token, { method: "DELETE" });
            setRows((r) => r.filter((x) => x.id !== id));
            toast.success("Enquiry deleted");
        } catch (err) {
            if (!err.session) toast.error("Could not delete enquiry");
        }
    };

    if (error) return <p className="rounded-2xl bg-red-50 p-4 text-sm text-red-600">{error}</p>;
    if (!rows) return <p className="text-sm text-slate-500">Loading…</p>;
    if (rows.length === 0)
        return (
            <p
                data-testid="admin-enquiries-empty"
                className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500"
            >
                No enquiries yet — they will appear here the moment a patient books.
            </p>
        );

    return (
        <div className="space-y-3">
            <button
                data-testid="admin-enquiries-refresh"
                onClick={load}
                className="mb-2 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-brand-navy transition-colors hover:border-brand-olive"
            >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
            </button>
            {rows.map((r) => (
                <div
                    key={r.id}
                    data-testid={`admin-enquiry-row-${r.id}`}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="font-bold text-brand-navy">{r.name}</p>
                            <p className="mt-0.5 font-mono text-xs text-slate-500">{fmtDate(r.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <StatusControl row={r} onSet={setStatus} />
                            <button
                                data-testid={`admin-enquiry-delete-${r.id}`}
                                onClick={() => remove(r.id)}
                                className="rounded-full border border-slate-200 p-2 text-slate-400 transition-colors hover:border-red-300 hover:text-red-500"
                                aria-label="Delete enquiry"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        <a href={`tel:+91${r.phone}`} className="rounded-full bg-brand-olivelight px-3 py-1.5 font-mono font-semibold text-brand-olive">
                            +91 {r.phone}
                        </a>
                        <span className="rounded-full bg-slate-100 px-3 py-1.5 font-medium text-brand-navy">{r.service}</span>
                        <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-600">{r.doctor}</span>
                        {r.preferred_date && (
                            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-600">Date: {r.preferred_date}</span>
                        )}
                    </div>
                    {(r.message || r.email) && (
                        <p className="mt-3 text-sm text-slate-600">
                            {r.message && <span className="italic">“{r.message}”</span>}
                            {r.email && <span className="ml-2 font-mono text-xs text-slate-400">{r.email}</span>}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

function BannerEditor({ token }) {
    const [form, setForm] = useState(null);

    useEffect(() => {
        apiFetch("/banner", null)
            .then(setForm)
            .catch(() => toast.error("Could not load banner"));
    }, []);

    if (!form) return <p className="text-sm text-slate-500">Loading…</p>;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        try {
            await apiFetch("/admin/banner", token, {
                method: "PUT",
                body: JSON.stringify({
                    enabled: !!form.enabled,
                    text_en: form.text_en,
                    text_mr: form.text_mr,
                    theme: form.theme,
                    link: form.link,
                }),
            });
            toast.success("Banner updated — live on the website now");
        } catch (err) {
            if (!err.session) toast.error("Could not save banner");
        }
    };

    return (
        <div className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <label className="flex items-center gap-3 text-sm font-semibold text-brand-navy">
                <input
                    data-testid="banner-enabled-checkbox"
                    type="checkbox"
                    checked={!!form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                    className="h-4 w-4 accent-brand-olive"
                />
                Show festival banner on the website
            </label>
            <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">Text — English</label>
                <textarea
                    data-testid="banner-text-en"
                    rows={2}
                    value={form.text_en}
                    onChange={set("text_en")}
                    placeholder="e.g. Diwali Dhamaka health camp — free skin check-up camp this Sunday!"
                    className="field-input resize-none"
                />
            </div>
            <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">Text — Marathi</label>
                <textarea
                    data-testid="banner-text-mr"
                    rows={2}
                    value={form.text_mr}
                    onChange={set("text_mr")}
                    placeholder="उदा. दिवाळी आरोग्य शिबिर — या रविवारी मोफत स्किन तपासणी!"
                    className="field-input resize-none"
                />
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">Theme</label>
                    <select data-testid="banner-theme-select" value={form.theme} onChange={set("theme")} className="field-input appearance-none">
                        <option value="default">Classic navy</option>
                        <option value="diwali">Diwali (warm amber)</option>
                        <option value="camp">Health camp (olive)</option>
                    </select>
                </div>
                <div>
                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">Optional link (https://)</label>
                    <input
                        data-testid="banner-link"
                        type="url"
                        value={form.link}
                        onChange={set("link")}
                        placeholder="https://www.signitawellness.in"
                        className="field-input"
                    />
                </div>
            </div>
            <button
                data-testid="banner-save-button"
                onClick={save}
                className="w-full rounded-full bg-brand-navy py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-olive"
            >
                Save & publish
            </button>
        </div>
    );
}

function GoogleEditor({ token }) {
    const [form, setForm] = useState(null);

    useEffect(() => {
        apiFetch("/google-info", null)
            .then(setForm)
            .catch(() => toast.error("Could not load Google info"));
    }, []);

    if (!form) return <p className="text-sm text-slate-500">Loading…</p>;

    const save = async () => {
        try {
            await apiFetch("/admin/google-info", token, {
                method: "PUT",
                body: JSON.stringify({
                    rating: Number(form.rating),
                    reviews: Number(form.reviews),
                }),
            });
            toast.success("Google rating updated — live on the website now");
        } catch (err) {
            if (!err.session) toast.error("Could not save — check the values");
        }
    };

    return (
        <div className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <p className="text-sm leading-relaxed text-slate-600">
                Update the star rating and review count shown on the homepage badge whenever new
                Google reviews come in — it updates on the website instantly.
            </p>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                    <label htmlFor="google-rating" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                        Star rating (0 – 5)
                    </label>
                    <input
                        id="google-rating"
                        data-testid="google-rating-input"
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        value={form.rating}
                        onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))}
                        className="field-input"
                    />
                </div>
                <div>
                    <label htmlFor="google-reviews" className="mb-2 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                        Total review count
                    </label>
                    <input
                        id="google-reviews"
                        data-testid="google-reviews-input"
                        type="number"
                        min="0"
                        value={form.reviews}
                        onChange={(e) => setForm((f) => ({ ...f, reviews: e.target.value }))}
                        className="field-input"
                    />
                </div>
            </div>
            <button
                data-testid="google-save-button"
                onClick={save}
                className="w-full rounded-full bg-brand-navy py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-olive"
            >
                Save & publish
            </button>
        </div>
    );
}

export default function AdminPage() {
    const [token, setToken] = useState(() => localStorage.getItem("signita-admin-token"));
    const [tab, setTab] = useState("enquiries");

    const logout = () => {
        localStorage.removeItem("signita-admin-token");
        setToken(null);
    };

    useEffect(() => {
        document.title = "Admin — Signita Wellness";
    }, []);

    if (!token) return <Login onLogin={setToken} />;

    return (
        <div className="min-h-screen bg-[#FAFAFA] pb-24">
            <Toaster position="top-center" richColors />
            <header className="border-b border-slate-200 bg-white/85 backdrop-blur-xl">
                <div className="mx-auto flex h-[72px] max-w-5xl items-center justify-between px-5 md:px-8">
                    <div className="flex items-center gap-3">
                        <Crescent className="h-7 w-7 text-brand-lime" />
                        <p className="font-display text-xl font-semibold text-brand-navy">Signita Admin</p>
                    </div>
                    <button
                        data-testid="admin-logout-button"
                        onClick={logout}
                        className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-500 transition-colors hover:border-red-300 hover:text-red-500"
                    >
                        <LogOut className="h-3.5 w-3.5" />
                        Log out
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-5 py-10 md:px-8">
                <div className="mb-8 flex flex-wrap gap-2">
                    {[
                        { id: "enquiries", label: "Enquiries", icon: ListChecks },
                        { id: "banner", label: "Festival Banner", icon: PartyPopper },
                        { id: "google", label: "Google Reviews", icon: Star },
                    ].map((tb) => (
                        <button
                            key={tb.id}
                            data-testid={`admin-tab-${tb.id}`}
                            onClick={() => setTab(tb.id)}
                            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold transition-all duration-300 md:text-sm ${
                                tab === tb.id
                                    ? "bg-brand-navy text-white"
                                    : "border border-slate-200 bg-white text-slate-500 hover:border-brand-olive hover:text-brand-olive"
                            }`}
                        >
                            <tb.icon className="h-4 w-4" />
                            {tb.label}
                        </button>
                    ))}
                    {tab !== "enquiries" && <Sparkles className="ml-auto mt-2.5 h-5 w-5 text-brand-lime" />}
                </div>

                {tab === "enquiries" && <Enquiries token={token} />}
                {tab === "banner" && <BannerEditor token={token} />}
                {tab === "google" && <GoogleEditor token={token} />}
            </main>
        </div>
    );
}
